import { ChainProtocol, READABLE_STANDARD, TokenStandardType } from '@archax/shared-types'
import EtherscanLogo from './assets/etherscan-logo-circle.svg'
import EtherlinkLogo from './assets/etherlink-logo.svg'
import ArbitrumLogo from './assets/arbitrum-logo.svg'
import AptosLogo from './assets/aptos-apt-logo.svg'
import HederaExplorerLogo from './assets/hedera-hashgraph-hbar.svg'
import PeraExplorerLogo from './assets/pera-explorer.svg'
import SolanaLogo from './assets/solana-logo.svg'
import StellarLogo from './assets/stellar-logo.svg'
import PolyscanLogo from './assets/polyscan-logo.svg'
import XRPLogo from './assets/xrp-logo.svg'
import XDCLogo from './assets/xdc-logo.png'
import SDRLogo from './assets/sd-icon-circle-blue.svg'

export const EXPLORER_LOGOS = {
  [ChainProtocol.Algorand]: PeraExplorerLogo,
  [ChainProtocol.Aptos]: AptosLogo,
  [ChainProtocol.Arbitrum]: ArbitrumLogo,
  [ChainProtocol.Ethereum]: EtherscanLogo,
  [ChainProtocol.Etherlink]: EtherlinkLogo,
  [ChainProtocol.Hedera]: HederaExplorerLogo,
  [ChainProtocol.Polygon]: PolyscanLogo,
  [ChainProtocol.Solana]: SolanaLogo,
  [ChainProtocol.Stellar]: StellarLogo,
  [ChainProtocol.XDC]: XDCLogo,
  [ChainProtocol.XRP_Ledger]: XRPLogo,
}

export function getExplorerLogo(protocol: ChainProtocol, privacyEnabled: boolean = false): string | undefined {
  if (privacyEnabled) {
    return SDRLogo
  } else {
    return EXPLORER_LOGOS[protocol]
  }
}

export const STANDARDS_WITH_PREMINT = [
  TokenStandardType.ASA,
  TokenStandardType.ERC20,
  TokenStandardType.FungibleCommon,
  TokenStandardType.ERC20PoolToken,
  TokenStandardType.FungibleCommonPool,
]

export const STANDARDS_WITH_DECIMALS = STANDARDS_WITH_PREMINT

export const PROTOCOLS_CAN_BE_CONFIGURED = [
  ChainProtocol.Arbitrum,
  ChainProtocol.Ethereum,
  ChainProtocol.Etherlink,
  ChainProtocol.Polygon,
  ChainProtocol.XDC,
]

export const PROTOCOLS_WITH_WHITELIST_REQUIRED = [
  ChainProtocol.Algorand,
  ChainProtocol.XRP_Ledger,
  ChainProtocol.Stellar,
]
export const PROTOCOLS_WITH_SUPPORT_TO_BLACKLIST = [
  ChainProtocol.Arbitrum,
  ChainProtocol.Aptos,
  ChainProtocol.Ethereum,
  ChainProtocol.Etherlink,
  ChainProtocol.Polygon,
  ChainProtocol.XDC,
  ChainProtocol.Hedera,
]

const commonStandardOptions = [TokenStandardType.FungibleCommon, TokenStandardType.NonFungibleUnique]
const evmStandardOptions = [TokenStandardType.ERC20, TokenStandardType.ERC721]
const evmPoolStandardOptions = [TokenStandardType.ERC20PoolToken]

export const protocolsAndStandard = {
  [ChainProtocol.Algorand]: {
    regular: [TokenStandardType.ASA],
    pool: [],
  },
  [ChainProtocol.Aptos]: {
    regular: [TokenStandardType.FungibleCommon],
    pool: [],
  },
  [ChainProtocol.Arbitrum]: {
    regular: evmStandardOptions,
    pool: evmPoolStandardOptions,
  },
  [ChainProtocol.Ethereum]: {
    regular: evmStandardOptions,
    pool: evmPoolStandardOptions,
  },
  [ChainProtocol.Etherlink]: {
    regular: evmStandardOptions,
    pool: evmPoolStandardOptions,
  },
  [ChainProtocol.Hedera]: {
    regular: commonStandardOptions,
    pool: [TokenStandardType.FungibleCommonPool],
  },
  [ChainProtocol.Solana]: {
    regular: commonStandardOptions,
    pool: [],
  },
  [ChainProtocol.Stellar]: {
    regular: commonStandardOptions,
    pool: [TokenStandardType.FungibleCommonPool],
  },
  [ChainProtocol.Polygon]: {
    regular: evmStandardOptions,
    pool: evmPoolStandardOptions,
  },
  [ChainProtocol.XDC]: {
    regular: evmStandardOptions,
    pool: evmPoolStandardOptions,
  },
  [ChainProtocol.XRP_Ledger]: {
    regular: [TokenStandardType.FungibleCommon],
    pool: [],
  },
}
export function getStandardsByProtocolOptions(protocol: ChainProtocol, isTokenRegular: boolean) {
  return (isTokenRegular ? protocolsAndStandard[protocol].regular : protocolsAndStandard[protocol].pool).map((it) => ({
    label: READABLE_STANDARD[it],
    value: it,
  }))
}
export function getRegularStandardsByProtocolOptions(protocol: ChainProtocol) {
  return protocolsAndStandard[protocol].regular.map((it) => ({ label: READABLE_STANDARD[it], value: it }))
}

export const INITIAL_OFFSET = 0
export const DEFAULT_PAGE_SIZE = 10
export const MAX_PAGE_SIZE = 100
export const maxOffsetAndLimit = {
  offset: INITIAL_OFFSET,
  limit: MAX_PAGE_SIZE,
}

export const proRataDecimals = process.env.REACT_APP_PRO_RATA_DECIMALS || '4'

export const TOKENS_DOCUMENTS_SIZE_LIMIT = 100 * 1024 * 1024 // 100MB
export const TOKENS_DOCUMENTS_SIZE_LIMIT_FORMATTED = '100MB'
export const ORGANIZATION_LOGO_SIZE_LIMIT = 500 * 1024 // 500KB
export const ORGANIZATION_LOGO_SIZE_LIMIT_FORMATTED = '500KB'

export const USDC_DECIMALS = 6

export const DISABLED_CHAINS = (process.env.REACT_APP_DISABLED_CHAINS || '')
  .split(',')
  .map((it) => ChainProtocol[it as keyof typeof ChainProtocol])

export const PROTOCOLS_WITH_PRIVACY_ENABLED = (process.env.REACT_APP_PROTOCOLS_WITH_PRIVACY_ENABLED || '')
  .replace(/"/i, '')
  .split(',')
  .map((it) => ChainProtocol[it as keyof typeof ChainProtocol])

export const PROTOCOLS_WITH_EXTERNAL_TOKEN_REQUIRES_STANDARD = [ChainProtocol.Aptos, ChainProtocol.Stellar]
export const PROTOCOLS_WITH_EXTERNAL_TOKEN_EDITABLE = [ChainProtocol.Stellar]
